/* eslint-disable no-underscore-dangle */
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import _debug from 'debug';
import makeAxiosRequest from 'lib/network/httplib/http_request';

const debug = _debug('app:adalRequest');

const msalConfig = {
  auth: {
    clientId: __CONFIG__.clientId,
    authority: `https://login.microsoftonline.com/${__CONFIG__.tenant}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

const getLoginScopes = () => ['openid', 'profile'];
const getApiScopes = () => [__CONFIG__.appScopeId];

const RETRY_FLAG = 'azureADTokenRetry';

const decodeJwt = (token) => {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch (error) {
    return null;
  }
};

const isTokenExpired = (token) => {
  const decoded = decodeJwt(token);
  if (!decoded || !decoded.exp) return true;
  return Date.now() >= decoded.exp * 1000;
};

// Called on app init — processes the Azure AD redirect response and sets the active account.
// Token acquisition happens on-demand via isAuthenticated() when API calls are made.
export const authenticate = () => (
  msalInstance.handleRedirectPromise()
    .then((response) => {
      if (response && response.account) {
        debug(response.account.name);
        msalInstance.setActiveAccount(response.account);
      }
    })
    .catch((error) => {
      debug('handleRedirectPromise error', error);
    })
);

// Mirrors auth.interceptor.ts: returns the stored token from localStorage
export const isAuthenticated = () => {
  const storedToken = localStorage.getItem('azureADToken');
  if (storedToken) {
    const token = decodeURIComponent(storedToken);
    if (!isTokenExpired(token)) {
      return Promise.resolve(token);
    }
  }
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return Promise.reject(new Error('No authenticated accounts'));
  }
  const account = msalInstance.getActiveAccount() || accounts[0];
  return msalInstance.acquireTokenSilent({ scopes: getApiScopes(), account })
    .then((tokenResponse) => {
      const token = tokenResponse.accessToken;
      localStorage.setItem('azureADToken', encodeURIComponent(token));
      return token;
    })
    .catch((error) => {
      if (error instanceof InteractionRequiredAuthError) {
        msalInstance.acquireTokenRedirect({ scopes: getApiScopes() });
      }
      return Promise.reject(error);
    });
};

export const getCachedUser = () => {
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
};

export const login = () => msalInstance.loginRedirect({ scopes: getLoginScopes() });

export const logout = () => {
  localStorage.removeItem('azureADToken');
  msalInstance.logoutRedirect();
};

// Make HTTP request — mirrors auth.interceptor.ts:
// reads Bearer token from localStorage, re-acquires and reloads on 401
export const adalRequest = settings => (
  new Promise((resolve, reject) => {
    isAuthenticated()
    .then((accessToken) => {
      if (!accessToken) return null;
      debug('Authenticated');
      debug('__CONFIG__', __CONFIG__);
      const headers = {};
      headers.Authorization = `Bearer ${accessToken}`;
      headers.Accept = 'application/json';
      headers.apiVersion = 1;
      headers['Content-Type'] = 'application/json; charset=utf-8';
      const user = getCachedUser();
      const userEmail = user ? user.username.toLowerCase() : '';
      let data;
      if (settings.apiName === 'fetch-details' && (settings.personid === '' || settings.personid === undefined)) {
        if (settings.modelName === 'lead-Pagination') {
          data = {
            modelName: settings.modelName,
            appId: __CONFIG__.envId,
            source: 'url',
            emailId: (settings.ownerId ?
              settings.ownerId.toLowerCase() : userEmail),
            paging: {
              page: settings.pageNumber
            },
          };
        } else {
          data = {
            modelName: settings.modelName,
            source: settings.source || 'url-fetch',
            appId: __CONFIG__.envId,
            emailId: (settings.ownerId ?
              settings.ownerId.toLowerCase() : userEmail),
            paging: {
              page: 1
            },
            lastModified: ''
          };
        }
      } else if (settings.personid !== '' && settings.personid !== undefined) {
        data = {
          modelName: settings.modelName,
          source: settings.source || 'url-fetch',
          appId: __CONFIG__.envId,
          emailId: (settings.ownerId ?
            settings.ownerId.toLowerCase() : userEmail),
          personId: settings.personid,
          paging: {
            page: 1
          },
          lastModified: ''
        };
      } else {
        data = {
          appId: __CONFIG__.envId,
          source: 'url',
          emailId: settings.ownerId || (user ? user.username : ''),
          data: settings.data
        };
      }

      if (settings.headers) {
        Object.assign(headers, settings.headers);
      }
      const fullURL = `${__CONFIG__.azureURI}/${settings.apiName}`;
      debug(`Make Axios Request - ${fullURL}`);
      return makeAxiosRequest(settings.method, fullURL, headers, data)
        .then((response) => {
          sessionStorage.removeItem(RETRY_FLAG);
          resolve(response);
        })
        .catch((error) => {
          if (error && error.response && error.response.status === 401) {
            if (sessionStorage.getItem(RETRY_FLAG)) {
              // Already retried once for this 401 and it failed again —
              // it isn't a stale token, so stop looping and force interactive login.
              sessionStorage.removeItem(RETRY_FLAG);
              login();
            } else {
              const accounts = msalInstance.getAllAccounts();
              if (accounts.length > 0) {
                const account = msalInstance.getActiveAccount() || accounts[0];
                sessionStorage.setItem(RETRY_FLAG, '1');
                msalInstance.acquireTokenSilent({ scopes: getApiScopes(), account })
                  .then((tokenResponse) => {
                    if (tokenResponse && tokenResponse.accessToken) {
                      localStorage.setItem('azureADToken', encodeURIComponent(tokenResponse.accessToken));
                      window.location.reload();
                    } else {
                      sessionStorage.removeItem(RETRY_FLAG);
                      login();
                    }
                  })
                  .catch((err) => {
                    sessionStorage.removeItem(RETRY_FLAG);
                    if (err instanceof InteractionRequiredAuthError) {
                      msalInstance.acquireTokenRedirect({ scopes: getApiScopes() });
                    } else {
                      login();
                    }
                  });
              } else {
                login();
              }
            }
          }
          reject(error);
        });
    })
    .catch((error) => {
      debug('Not Authenticated');
      if (!getCachedUser()) {
        login();
      }
      reject(error);
    });
  })
);



// WEBPACK FOOTER //
// ./src/lib/network/webapi/adal/adal_request.js